/**
 * Village / Town building layout — card wireframe between HUD and nav.
 * Coordinates are in design-resolution space (390×844).
 */

export type VillageBuildingId =
  | 'dungeon_gate'
  | 'training_grounds'
  | 'blacksmith'
  | 'tavern'
  | 'shrine';

export interface VillageBuildingDef {
  id: VillageBuildingId;
  label: string;
  /** Scene key to open. */
  sceneKey: string;
  /** Hotspot center X. */
  x: number;
  /** Hotspot center Y. */
  y: number;
  /** Touch target width (min ~64 for thumbs). */
  width: number;
  /** Touch target height. */
  height: number;
  /** Card fill for wireframe placeholder. */
  color: number;
  /** Accent / border color. */
  accent: number;
  /** Short status chip under the label. */
  status: 'open' | 'foundation' | 'coming_soon';
  statusLabel: string;
  /** Wireframe icon caption (no art pack). */
  iconLabel: string;
}

/**
 * Five building cards between HUD (~96) and nav (~762).
 * Gate top-centre; Grounds mid-left; Blacksmith mid-right;
 * Tavern / Shrine lower. Large touch targets for portrait thumbs.
 */
export const VILLAGE_BUILDINGS: readonly VillageBuildingDef[] = [
  {
    id: 'dungeon_gate',
    label: 'Dungeon Gate',
    sceneKey: 'DungeonGate',
    x: 195,
    y: 200,
    width: 168,
    height: 112,
    color: 0x243850,
    accent: 0x5a8acc,
    status: 'open',
    statusLabel: 'Enter',
    iconLabel: 'GATE',
  },
  {
    id: 'training_grounds',
    label: 'Training Grounds',
    sceneKey: 'TrainingGrounds',
    x: 108,
    y: 360,
    width: 150,
    height: 112,
    color: 0x1e3a28,
    accent: 0x4aaa6a,
    status: 'foundation',
    statusLabel: 'Progress',
    iconLabel: 'TRAIN',
  },
  {
    id: 'blacksmith',
    label: 'Blacksmith',
    sceneKey: 'Blacksmith',
    x: 282,
    y: 360,
    width: 150,
    height: 112,
    color: 0x3a2818,
    accent: 0xd48830,
    status: 'foundation',
    statusLabel: 'Gear',
    iconLabel: 'FORGE',
  },
  {
    id: 'tavern',
    label: 'Tavern',
    sceneKey: 'ComingSoon',
    x: 108,
    y: 520,
    width: 150,
    height: 112,
    color: 0x3a181c,
    accent: 0xc05058,
    status: 'coming_soon',
    statusLabel: 'Coming Soon',
    iconLabel: 'INN',
  },
  {
    id: 'shrine',
    label: 'Shrine',
    sceneKey: 'ComingSoon',
    x: 282,
    y: 520,
    width: 150,
    height: 112,
    color: 0x281830,
    accent: 0x9a68c8,
    status: 'coming_soon',
    statusLabel: 'Coming Soon',
    iconLabel: 'SHRINE',
  },
] as const;

export function buildingById(
  id: VillageBuildingId,
): VillageBuildingDef | undefined {
  return VILLAGE_BUILDINGS.find((b) => b.id === id);
}
